<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" sm="12" md="6" lg="6" xl="4" v-for="item in wrapItems">
        <v-remocon
            :value="item"
            @on-remocon="onRemocon(item)"
            @on-button="buttonId => onButton(item.id, buttonId)"
            @on-command="commandId => onCommand(item.id, commandId)"
            @on-button-rearrange="param => onButtonRearrange(item.id, param)"
            @on-command-rearrange="param => onCommandRearrange(item.id, param)"
        ></v-remocon>
      </v-col>
    </v-row>
    <v-dialog v-model="dialog.remocon.isOpened" class="w-75" @close="dialog.remocon.onCancel">
      <v-card>
        <v-card-title>{{ dialog.remocon.id === CREATE_REMOCON_NAME ? '리모콘 추가' : '리모콘 수정' }}</v-card-title>
        <v-card-item>
          <v-text-field v-model="dialog.remocon.name" variant="filled" placeholder="리모콘의 타입을 입력하세요."></v-text-field>
        </v-card-item>
        <v-card-actions class="d-flex justify-end">
          <v-btn variant="tonal" @click="dialog.remocon.onCancel">취소</v-btn>
          <v-btn variant="tonal" @click="dialog.remocon.onOk">확인</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialog.command.isOpened" class="w-75" @close="dialog.command.onCancel">
      <v-card>
        <v-card-title>{{ dialog.command.id === CREATE_COMMAND_NAME ? '명령 추가' : '명령 수정' }}</v-card-title>
        <v-card-item>
          <v-text-field v-model="dialog.command.name" variant="filled" placeholder="명령의 이름을 입력하세요."></v-text-field>
          <v-textarea v-model="dialog.command.exec" variant="filled" placeholder="실행구문을 입력하세요."></v-textarea>
        </v-card-item>
        <v-card-actions class="d-flex">
          <v-btn variant="tonal" v-if="dialog.command.id !== CREATE_COMMAND_NAME" @click="dialog.command.onDelete">삭제</v-btn>
          <v-spacer></v-spacer>
          <v-btn variant="tonal" @click="dialog.command.onCancel">취소</v-btn>
          <v-btn variant="tonal" @click="dialog.command.onOk">확인</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-button-dialog
        v-model="dialog.button"
        v-if="dialog.button.isOpened"
        @on-delete="dialog.button.onDelete"
        @on-cancel="dialog.button.onCancel"
        @on-ok="dialog.button.onOk"
    ></v-button-dialog>
  </v-container>
</template>

<script setup>

import {computed, inject, onMounted, reactive} from "vue";
import VRemocon from "@/components/VRemocon.vue";
import VButtonDialog from "@/components/VButtonDialog.vue";
import {CREATE_BUTTON_NAME, CREATE_COMMAND_NAME, CREATE_REMOCON_NAME} from "@/common/constants";
import {useAppStore} from "@/stores/app";

const axios = inject('axios');
const appStore = useAppStore();

const dialog = reactive({
  remocon: {
    isOpened: false,
  },
  button: {
    isOpened: false,
    steps: [],
    commands: [],
  },
  command: {
    isOpened: false,
  }
});

const wrapItems = computed(() => [...appStore.remocons, {id: CREATE_REMOCON_NAME}].map(remocon => ({
  ...remocon,
  editable: false,
  buttons: remocon.buttons && [...remocon.buttons, reactive({
    id: CREATE_BUTTON_NAME,
  })],
  commands: remocon.commands && [...remocon.commands, reactive({
    id: CREATE_COMMAND_NAME,
  })],
})));

// onMounted(() => {
//   appStore.loadRemocons();
// });

const onRemocon = async remocon => {
  const verify = () => {
    if (0 === dialog.remocon.name.trim().length) {
      alert('리모콘의 타입을 입력하세요.');
      return false;
    }
    return true;
  };

  const done = await new Promise(r => dialog.remocon = {
    isOpened: true,
    id: remocon.id,
    name: remocon.name || '',
    onCancel: () => r(false),
    onOk: () => verify() && r(true),
  });
  dialog.remocon.isOpened = false;
  if (!done) return;

  if (remocon.id === CREATE_REMOCON_NAME) {
    try {
      const response = await axios.post(`/api/remocon`, {
        name: dialog.remocon.name,
        buttons: [],
        commands: [],
      });
      if (!response || 200 !== response.status || 0 !== response.data.code) return;

      await appStore.loadRemocons();

    } catch (error) {
      console.log(error);
    }
    return;
  }

  try {
    const response = await axios.put(`/api/remocon/${remocon.id}`, {
      ...remocon,
      name: dialog.remocon.name,
    });
    if (!response || 200 !== response.status || 0 !== response.data.code) return;

    await appStore.loadRemocons();

  } catch (error) {
    console.log(error);
  }
};

const onButton = async (remoconId, buttonId) => {
  const remocon = appStore.remocons.find(({id}) => id === remoconId);
  if (!remocon) return;

  const button = remocon.buttons && remocon.buttons.find(({id}) => id === buttonId);

  const action = await new Promise(r => dialog.button = {
    isOpened: true,
    id: buttonId,
    name: button && button.name || '',
    description: button && button.description || '',
    steps: button && button.steps || [],
    commands: remocon.commands,
    onDelete: () => r('delete'),
    onCancel: () => r(),
    onOk: () => r('ok'),
  });

  dialog.button.isOpened = false;
  if (!action) return;

  const buttons = (() => {
    if (!button) return [
      ...remocon.buttons, {
        name: dialog.button.name,
        description: dialog.button.description,
        steps: dialog.button.steps,
      }
    ];

    if ('delete' === action) return remocon.buttons.filter(({id}) => id !== buttonId);

    return remocon.buttons.map(button => button.id !== buttonId ? button : ({
      ...button,
      name: dialog.button.name,
      description: dialog.button.description,
      steps: dialog.button.steps,
    }));
  })();

  try {
    const response = await axios.put(`/api/remocon/${remoconId}`, {
      ...remocon,
      buttons: buttons.map(({id, name, description, steps}) => ({
        id, name, description, stepsJson: JSON.stringify(steps),
      })),
    });
    if (!response || 200 !== response.status || 0 !== response.data.code) return;

    await appStore.loadRemocons();

  } catch (error) {
    console.log(error);
  }
};

const onCommand = async (remoconId, commandId) => {
  const remocon = appStore.remocons.find(({id}) => id === remoconId);
  if (!remocon) return;

  const command = remocon.commands.find(({id}) => id === commandId);

  const verify = () => {
    try {
      JSON.parse(dialog.command.exec)
    } catch (error) {
      alert('올바른 JSON 형식으로 입력하세요.');
      return false;
    }
    return true;
  };

  const action = await new Promise(r => dialog.command = {
    isOpened: true,
    id: commandId,
    name: command && command.name || '',
    exec: command && command.exec || '',
    onDelete: () => r('delete'),
    onCancel: () => r(),
    onOk: () => verify() && r('ok'),
  });

  dialog.command.isOpened = false;
  if (!action) return;

  const commands = (() => {
    if (!command) return [
      ...remocon.commands, {
        name: dialog.command.name,
        exec: dialog.command.exec,
      }
    ];

    if ('delete' === action) return remocon.commands.filter(({id}) => id !== commandId);

    return remocon.commands.map(command => command.id !== commandId ? command : ({
      ...command,
      name: dialog.command.name,
      exec: dialog.command.exec,
    }));
  })();

  try {
    const response = await axios.put(`/api/remocon/${remoconId}`, {
      ...remocon,
      commands,
    });
    if (!response || 200 !== response.status || 0 !== response.data.code) return;

    await appStore.loadRemocons();

  } catch (error) {
    console.log(error);
  }
};

const onButtonRearrange = async (remoconId, param) => {
  const remocon = appStore.remocons.find(({id}) => id === remoconId);
  if (!remocon) return;

  const {newIndex, oldIndex} = param;
  if (oldIndex < 0 || remocon.buttons.length <= oldIndex) return;
  if (newIndex < 0 || remocon.buttons.length <= newIndex) return;

  const button = remocon.buttons[oldIndex];
  const buttons = remocon.buttons.filter((_, index) => index !== oldIndex);
  buttons.splice(newIndex, 0, button);

  try {
    const response = await axios.put(`/api/remocon/${remocon.id}`, {
      ...remocon,
      buttons,
    });
    if (!response || 200 !== response.status || 0 !== response.data.code) return;

    await appStore.loadRemocons();

  } catch (error) {
    console.log(error);
  }
};

const onCommandRearrange = async (remoconId, param) => {
  const remocon = appStore.remocons.find(({id}) => id === remoconId);
  if (!remocon) return;

  const {newIndex, oldIndex} = param;
  if (oldIndex < 0 || remocon.commands.length <= oldIndex) return;
  if (newIndex < 0 || remocon.commands.length <= newIndex) return;

  const command = remocon.commands[oldIndex];
  const commands = remocon.commands.filter((_, index) => index !== oldIndex);
  commands.splice(newIndex, 0, command);

  try {
    const response = await axios.put(`/api/remocon/${remocon.id}`, {
      ...remocon,
      commands,
    });
    if (!response || 200 !== response.status || 0 !== response.data.code) return;

    await appStore.loadRemocons();

  } catch (error) {
    console.log(error);
  }
};

</script>

<style scoped>

</style>
